/**
 * CRM Filters Hook
 *
 * URL-synced filter state management for the pipeline page
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PipelineFilters, OpportunityType, PipelineType } from '../types';

interface UseCrmFiltersResult {
  filters: PipelineFilters;
  setFilter: <K extends keyof PipelineFilters>(key: K, value: PipelineFilters[K] | undefined) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

/**
 * Hook for managing pipeline filters with URL sync
 */
export function useCrmFilters(): UseCrmFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: PipelineFilters = useMemo(
    () => ({
      date_from: searchParams.get('from') || undefined,
      date_to: searchParams.get('to') || undefined,
      owner_id: searchParams.get('owner') || undefined,
      account_id: searchParams.get('account') || undefined,
      account_manager: searchParams.get('manager') || undefined,
      type: (searchParams.get('type') as OpportunityType) || undefined,
      pipeline: (searchParams.get('pipeline') as PipelineType) || undefined,
      min_value: searchParams.get('minValue') ? Number(searchParams.get('minValue')) : undefined,
      max_value: searchParams.get('maxValue') ? Number(searchParams.get('maxValue')) : undefined,
      search: searchParams.get('q') || undefined,
    }),
    [searchParams]
  );

  const setFilter = useCallback(
    <K extends keyof PipelineFilters>(key: K, value: PipelineFilters[K] | undefined) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        // Map internal keys to URL param keys
        const paramKeyMap: Record<keyof PipelineFilters, string> = {
          date_from: 'from',
          date_to: 'to',
          owner_id: 'owner',
          account_id: 'account',
          account_manager: 'manager',
          type: 'type',
          pipeline: 'pipeline',
          min_value: 'minValue',
          max_value: 'maxValue',
          search: 'q',
        };

        const paramKey = paramKeyMap[key];

        if (value !== undefined && value !== null && value !== '') {
          newParams.set(paramKey, String(value));

          // Auto-clear conflicting value range to prevent invalid queries
          if (key === 'min_value' && typeof value === 'number') {
            const maxValue = newParams.get('maxValue');
            if (maxValue && value > Number(maxValue)) {
              newParams.delete('maxValue');
            }
          }
          if (key === 'max_value' && typeof value === 'number') {
            const minValue = newParams.get('minValue');
            if (minValue && value < Number(minValue)) {
              newParams.delete('minValue');
            }
          }
        } else {
          newParams.delete(paramKey);
        }

        return newParams;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((v) => v !== undefined && v !== '' && v !== null);
  }, [filters]);

  return {
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  };
}
